import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { type IUserResponse } from "../api/auth";
import { applyResetMiddleware } from "./middleware/resetMiddleware";

type AuthHeaderField = {
  Authorization: string;
};

interface AuthState {
  user: IUserResponse | null;
  basicAuthHeader?: AuthHeaderField;
  oAuthHeader?: AuthHeaderField;
  isInitialized: boolean;

  actions: {
    setUser: (payload: IUserResponse) => void;
    clearUser: () => void;
    setBasicAuthHeader: (authHeaderField: AuthState["basicAuthHeader"]) => void;
    resetBasicAuthHeader: () => void;
    setOAuthHeader: (authHeaderField: AuthState["oAuthHeader"]) => void;
    resetOAuthHeader: () => void;
    setCommonAuthState: (payload: Partial<Omit<AuthState, "actions">>) => void;
    resetCommonAuthState: () => void;
  };
}

const actionName = actionNameBuilder("auth");

export const useAuthStore = create<AuthState>()(
  devtools(
    /**
     * @TODO: add data encryption when persisting store
     * */
    persist(
      immer(
        applyResetMiddleware((set, _, store) => ({
          user: null,
          isInitialized: false,
          oAuthHeader: undefined,
          basicAuthHeader: undefined,

          // Separate namespace for store actions
          actions: {
            setUser: (payload) => set(() => ({ user: payload }), undefined, actionName("setUser")),

            clearUser: () => set(() => ({ user: null }), undefined, actionName("clearUser")),

            setBasicAuthHeader: (payload) => set(() => ({ basicAuthHeader: payload }), undefined, actionName("setBasicAuthHeader")),

            resetBasicAuthHeader: () => set(() => ({ basicAuthHeader: undefined }), undefined, actionName("resetBasicAuthHeader")),

            setOAuthHeader: (payload) => set(() => ({ oAuthHeader: payload }), undefined, actionName("setOAuthHeader")),

            resetOAuthHeader: () => set(() => ({ oAuthHeader: undefined }), undefined, actionName("resetOAuthHeader")),

            setCommonAuthState: (payload) => set(() => ({ ...payload }), undefined, actionName("setCommonAuthState")),

            resetCommonAuthState: () => set(store.getInitialState(), undefined, actionName("resetCommonAuthState"))
          }
        }))
      ),
      { name: "auth-user", partialize: (ctx) => ({ user: ctx.user }) }
    ),
    {
      name: "authStore"
    }
  )
);

// Hook for store states
export const useAuthSelector = () =>
  useAuthStore(
    useShallow((state) => ({
      user: state.user,
      isInitialized: state.isInitialized,
      oAuthHeader: state.oAuthHeader,
      basicAuthHeader: state.basicAuthHeader
    }))
  );

// Hook for store actions
export const useAuthActions = () => useAuthStore((state) => state.actions);
